/**
 * Backend health probing for the API-outage gate.
 *
 * `probeApiVersion` pings `/version/` with a raw fetch (deliberately NOT the
 * axios client, so a failing probe can't re-enter the error interceptor and
 * loop). `reportApiFailure` is called from that interceptor on a network error
 * or 5xx: it confirms a real outage with a single deduped probe before flipping
 * the store to "down", so a one-off endpoint 5xx (while the backend is fine)
 * doesn't trigger the reconnect overlay.
 *
 * Contains:
 * - probeApiVersion — one raw `/version/` check, resolves to a boolean
 * - reportApiFailure — confirm-then-mark-down, deduped
 */
import { API_URL } from '@sophocles/config';

import { setApiDown, selectApiDown } from './slice';

const VERSION_URL = `${API_URL}/version/`;
const PROBE_TIMEOUT_MS = 8000;

/**
 * One health probe of `/version/`. Returns true iff the backend answered 2xx.
 * Uses a raw fetch so it bypasses the axios interceptor entirely.
 */
export async function probeApiVersion(timeoutMs = PROBE_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(VERSION_URL, { method: 'GET', cache: 'no-store', signal: controller.signal });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

let probing = false;

/**
 * Called from the axios error interceptor on a network error / 5xx. Confirms
 * the backend is actually unreachable with a single (deduped) `/version/`
 * probe, and only then marks the API down so the ApiHealthGate takes over.
 *
 * @param {import('redux').Store} store
 */
export async function reportApiFailure(store) {
  if (probing || selectApiDown(store.getState())) return;
  probing = true;
  try {
    const ok = await probeApiVersion();
    if (!ok) store.dispatch(setApiDown());
  } finally {
    probing = false;
  }
}
